import * as React from "react";
import {
  Card,
  Button,
  Spinner,
  NonIdealState,
  FormGroup,
  InputGroup,
  TextArea,
  HTMLTable,
  HTMLSelect,
  Menu,
  MenuItem,
  Popover,
  Dialog,
  Tag,
  Tabs,
  Tab,
  TabPanel,
  Switch,
  Icon,
  type OverlayToaster,
} from "@blueprintjs/core";
import { css } from "@emotion/core";
import ApiAdmin, {
  IMaterial,
  ISite,
  IOrderType,
  IChartProperty,
  IOrgChartPropertyAssignment,
} from "../../../api/admin";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useOutletContext } from "react-router-dom";
import { ConfirmationDialog } from "../common/ConfirmationDialog";
import { StringListEditor } from "../common/StringListEditor";
import { OrderTypeItemsEditor } from "../common/OrderTypeItemsEditor";
import { OrderTypesPanel } from "./OrderTypesPanel";
import { ChartPropertiesPanel } from "./ChartPropertiesPanel";
import { DEFAULT_BASEMAP, mapStyles } from "../../../context/map";
import { roundedHoursFromSeconds } from "../../../utils/format";
import {
  isValidInviteDomain,
  normalizeInviteDomain,
} from "../../../../../smokepit/src/utils/inviteDomains";
import { mergePendingDomainDraft } from "./organizationSettingsHelpers";
import { ReadOnlyNotice } from "../common/ReadOnlyNotice";
import { readOnlyNoticeCopy } from "../common/readOnlyNoticeCopy";

const ORGANIZATION_EDIT_VALID_TABS: string[] = [
  "settings",
  "materials",
  "sites",
  "orderTypes",
  "chartProperties",
];

function resolveOrganizationEditTab(tabParam: string | null): string {
  return tabParam && ORGANIZATION_EDIT_VALID_TABS.includes(tabParam)
    ? tabParam
    : "settings";
}

function MaterialEditDialog(props: {
  editMaterial: IMaterial | null;
  isCreate: boolean;
  onClose: () => void;
  organizationId: number;
}) {
  const { editMaterial, isCreate, onClose, organizationId } = props;

  const [editErrors, setEditErrors] = React.useState<string[]>([]);

  const queryClient = useQueryClient();
  const { toaster } = useOutletContext<{
    toaster: React.RefObject<OverlayToaster>;
  }>();

  const createMaterialMutation = useMutation({
    mutationFn: ({ name, inventory }: { name: string; inventory: boolean }) =>
      ApiAdmin.createMaterial(organizationId, { name, inventory }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["materials"] });
      if (toaster.current) {
        toaster.current.show({
          message: "Material created successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
      onClose();
    },
    onError: (error) => {
      if (toaster.current) {
        toaster.current.show({
          message:
            error.message || "Failed to create material. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
  });

  const updateMaterialMutation = useMutation({
    mutationFn: ({
      materialId,
      name,
      inventory,
    }: {
      materialId: number;
      name: string;
      inventory: boolean;
    }) =>
      ApiAdmin.updateMaterial(organizationId, materialId, { name, inventory }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["materials"] });
      if (toaster.current) {
        toaster.current.show({
          message: "Material updated successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
      onClose();
    },
    onError: (error) => {
      if (toaster.current) {
        toaster.current.show({
          message:
            error.message || "Failed to update material. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const errors: string[] = [];
    const nameInput = event.currentTarget.elements.namedItem(
      "material-name"
    ) as HTMLInputElement;
    const inventoryInput = event.currentTarget.elements.namedItem(
      "inventory"
    ) as HTMLInputElement;

    const name = nameInput?.value;
    const inventory = inventoryInput?.checked ?? true;

    if (!name || name.trim() === "") {
      errors.push("name");
    }

    if (errors.length === 0) {
      if (isCreate) {
        createMaterialMutation.mutate({ name: name.trim(), inventory });
      } else if (editMaterial) {
        updateMaterialMutation.mutate({
          materialId: editMaterial.id,
          name: name.trim(),
          inventory,
        });
      }
    } else {
      setEditErrors(errors);
    }
  };

  return (
    <Dialog
      className="bp6-dark"
      css={css`
        width: 524px;
      `}
      title={isCreate ? "Create Material" : "Edit Material"}
      isOpen={editMaterial !== null || isCreate}
      onClose={() => {
        if (
          !createMaterialMutation.isPending &&
          !updateMaterialMutation.isPending
        ) {
          onClose();
        }
      }}
    >
      <form
        css={css`
          padding: 1rem;
        `}
        onSubmit={handleSubmit}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 1rem;
            margin-bottom: 1rem;
          `}
        >
          <FormGroup
            label="Name"
            labelFor="material-name"
            labelInfo="(required)"
            intent={editErrors.includes("name") ? "danger" : "none"}
          >
            <InputGroup
              id="material-name"
              intent={editErrors.includes("name") ? "danger" : "none"}
              disabled={
                createMaterialMutation.isPending ||
                updateMaterialMutation.isPending
              }
              defaultValue={editMaterial?.name || ""}
              autoFocus
            />
          </FormGroup>
          <FormGroup label="Inventory" labelFor="material-inventory">
            <Switch
              id="material-inventory"
              name="inventory"
              defaultChecked={editMaterial?.inventory ?? true}
              disabled={
                createMaterialMutation.isPending ||
                updateMaterialMutation.isPending
              }
            />
          </FormGroup>
        </div>

        <div
          css={css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            padding-top: 0.5rem;
          `}
        >
          <div
            css={css`
              color: var(--danger);
              flex-grow: 1;
            `}
          >
            {editErrors.length > 0 &&
              `Missing or invalid fields: ${editErrors.join(", ")}.`}
          </div>
          <div
            css={css`
              display: flex;
              justify-content: end;
              gap: 1rem;
            `}
          >
            <Button
              text="Cancel"
              disabled={
                createMaterialMutation.isPending ||
                updateMaterialMutation.isPending
              }
              intent="none"
              onClick={() => {
                if (
                  !createMaterialMutation.isPending &&
                  !updateMaterialMutation.isPending
                ) {
                  onClose();
                }
              }}
            />
            <Button
              text={
                createMaterialMutation.isPending ||
                updateMaterialMutation.isPending
                  ? "Saving..."
                  : isCreate
                    ? "Create"
                    : "Save"
              }
              disabled={
                createMaterialMutation.isPending ||
                updateMaterialMutation.isPending
              }
              intent="primary"
              type="submit"
            />
          </div>
        </div>
      </form>
    </Dialog>
  );
}

function DisableMaterialDialog(props: {
  material: IMaterial | null;
  onClose: () => void;
  organizationId: number;
}) {
  const { material, onClose, organizationId } = props;

  const queryClient = useQueryClient();
  const { toaster } = useOutletContext<{
    toaster: React.RefObject<OverlayToaster>;
  }>();

  const disableMaterialMutation = useMutation({
    mutationFn: (materialId: number) =>
      ApiAdmin.updateMaterial(organizationId, materialId, {
        archived: Date.now(),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["materials"] });
      if (toaster.current) {
        toaster.current.show({
          message: "Material deactivated successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
      onClose();
    },
    onError: (error) => {
      if (toaster.current) {
        toaster.current.show({
          message:
            error.message || "Failed to deactivate material. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
  });

  return (
    <ConfirmationDialog
      key={
        material ? "disable-material-dialog" : "disable-material-dialog-closed"
      }
      entityName={material?.name || ""}
      entityType="Material"
      message="Deactivated materials can't be added to new layers."
      isOpen={material !== null}
      onClose={onClose}
      onConfirm={() => material && disableMaterialMutation.mutate(material.id)}
      isPending={disableMaterialMutation.isPending}
      actionText="Deactivate"
      actioningText="Deactivating..."
      titleAction="Deactivate"
      icon="disable"
    />
  );
}

function SiteEditDialog(props: {
  editSite: ISite | null;
  isCreate: boolean;
  onClose: () => void;
  organizationId: number;
}) {
  const { editSite, isCreate, onClose, organizationId } = props;

  const [editErrors, setEditErrors] = React.useState<string[]>([]);

  const queryClient = useQueryClient();
  const { toaster } = useOutletContext<{
    toaster: React.RefObject<OverlayToaster>;
  }>();

  const createSiteMutation = useMutation({
    mutationFn: ({
      label,
      location,
    }: {
      label: string;
      location?: { latitude: number; longitude: number };
    }) => ApiAdmin.createSite(organizationId, { label, location }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sites"] });
      queryClient.invalidateQueries({
        queryKey: ["organization", organizationId],
      });
      if (toaster.current) {
        toaster.current.show({
          message: "Site created successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
      onClose();
    },
    onError: (error) => {
      if (toaster.current) {
        toaster.current.show({
          message: error.message || "Failed to create site. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
  });

  const updateSiteMutation = useMutation({
    mutationFn: ({
      siteId,
      label,
      location,
    }: {
      siteId: number;
      label: string;
      location?: { latitude: number; longitude: number };
    }) => ApiAdmin.updateSite(organizationId, siteId, { label, location }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sites"] });
      queryClient.invalidateQueries({
        queryKey: ["organization", organizationId],
      });
      if (toaster.current) {
        toaster.current.show({
          message: "Site updated successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
      onClose();
    },
    onError: (error) => {
      if (toaster.current) {
        toaster.current.show({
          message: error.message || "Failed to update site. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const errors: string[] = [];
    const labelInput = event.currentTarget.elements.namedItem(
      "site-label"
    ) as HTMLInputElement;
    const latitudeInput = event.currentTarget.elements.namedItem(
      "latitude"
    ) as HTMLInputElement;
    const longitudeInput = event.currentTarget.elements.namedItem(
      "longitude"
    ) as HTMLInputElement;

    const label = labelInput?.value;
    const latitudeStr = latitudeInput?.value;
    const longitudeStr = longitudeInput?.value;

    if (!label || label.trim() === "") {
      errors.push("label");
    }

    // Validate and parse location
    let locationValue: { latitude: number; longitude: number } | undefined;
    const hasLatitude = latitudeStr && latitudeStr.trim() !== "";
    const hasLongitude = longitudeStr && longitudeStr.trim() !== "";

    if (hasLatitude || hasLongitude) {
      if (!hasLatitude || !hasLongitude) {
        errors.push("latitude");
        errors.push("longitude");
      } else {
        const latitude = parseFloat(latitudeStr);
        const longitude = parseFloat(longitudeStr);

        if (isNaN(latitude) || isNaN(longitude)) {
          errors.push("latitude");
          errors.push("longitude");
        } else {
          if (latitude < -90 || latitude > 90) {
            errors.push("latitude");
          }
          if (longitude < -180 || longitude > 180) {
            errors.push("longitude");
          }

          if (!errors.includes("latitude") && !errors.includes("longitude")) {
            locationValue = { latitude, longitude };
          }
        }
      }
    }

    if (errors.length === 0) {
      const mutationData: {
        label: string;
        location?: { latitude: number; longitude: number };
      } = {
        label: label.trim(),
      };

      if (locationValue !== undefined) {
        mutationData.location = locationValue;
      }

      if (isCreate) {
        createSiteMutation.mutate(mutationData);
      } else if (editSite) {
        updateSiteMutation.mutate({
          siteId: editSite.id,
          ...mutationData,
        });
      }
    } else {
      setEditErrors(errors);
    }
  };

  return (
    <Dialog
      className="bp6-dark"
      css={css`
        width: 524px;
      `}
      title={isCreate ? "Create Site" : "Edit Site"}
      isOpen={editSite !== null || isCreate}
      onClose={() => {
        if (!createSiteMutation.isPending && !updateSiteMutation.isPending) {
          onClose();
        }
      }}
    >
      <form
        css={css`
          padding: 1rem;
        `}
        onSubmit={handleSubmit}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 1rem;
            margin-bottom: 1rem;
          `}
        >
          <FormGroup
            label="Label"
            labelFor="site-label"
            labelInfo="(required)"
            intent={editErrors.includes("label") ? "danger" : "none"}
          >
            <InputGroup
              id="site-label"
              intent={editErrors.includes("label") ? "danger" : "none"}
              disabled={
                createSiteMutation.isPending || updateSiteMutation.isPending
              }
              defaultValue={editSite?.label || ""}
              autoFocus
            />
          </FormGroup>
          <div
            css={css`
              display: flex;
              gap: 1rem;
            `}
          >
            <FormGroup
              label="Latitude"
              labelFor="site-latitude"
              labelInfo="(-90 to 90)"
              intent={editErrors.includes("latitude") ? "danger" : "none"}
            >
              <InputGroup
                id="site-latitude"
                name="latitude"
                type="number"
                min={-90}
                max={90}
                step="any"
                defaultValue={
                  editSite?.location?.coordinates[1]?.toString() || ""
                }
                disabled={
                  createSiteMutation.isPending || updateSiteMutation.isPending
                }
                placeholder="Enter latitude"
              />
            </FormGroup>

            <FormGroup
              label="Longitude"
              labelFor="site-longitude"
              labelInfo="(-180 to 180)"
              intent={editErrors.includes("longitude") ? "danger" : "none"}
            >
              <InputGroup
                id="site-longitude"
                name="longitude"
                type="number"
                min={-180}
                max={180}
                step="any"
                defaultValue={
                  editSite?.location?.coordinates[0]?.toString() || ""
                }
                disabled={
                  createSiteMutation.isPending || updateSiteMutation.isPending
                }
                placeholder="Enter longitude"
              />
            </FormGroup>
          </div>
        </div>

        <div
          css={css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            padding-top: 0.5rem;
          `}
        >
          <div
            css={css`
              color: var(--danger);
              flex-grow: 1;
            `}
          >
            {editErrors.length > 0 &&
              `Missing or invalid fields: ${editErrors.join(", ")}.`}
          </div>
          <div
            css={css`
              display: flex;
              justify-content: end;
              gap: 1rem;
            `}
          >
            <Button
              text="Cancel"
              disabled={
                createSiteMutation.isPending || updateSiteMutation.isPending
              }
              intent="none"
              onClick={() => {
                if (
                  !createSiteMutation.isPending &&
                  !updateSiteMutation.isPending
                ) {
                  onClose();
                }
              }}
            />
            <Button
              text={
                createSiteMutation.isPending || updateSiteMutation.isPending
                  ? "Saving..."
                  : isCreate
                    ? "Create"
                    : "Save"
              }
              disabled={
                createSiteMutation.isPending || updateSiteMutation.isPending
              }
              intent="primary"
              type="submit"
            />
          </div>
        </div>
      </form>
    </Dialog>
  );
}

function SettingsPanel(props: {
  organization: any;
  updateOrganizationSettingsMutation: any;
  handleOrganizationSubmit: (
    event: React.FormEvent<HTMLFormElement>,
    allowedInviteDomains: string[]
  ) => void;
}) {
  const {
    organization,
    updateOrganizationSettingsMutation,
    handleOrganizationSubmit,
  } = props;

  const [allowedInviteDomains, setAllowedInviteDomains] = React.useState<
    string[]
  >(() => organization.allowed_invite_domains ?? []);

  return (
    <Card>
      <form
        onSubmit={(event) =>
          handleOrganizationSubmit(event, allowedInviteDomains)
        }
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 1rem;
            margin-bottom: 1rem;
          `}
        >
          <div
            css={css`
              max-width: 256px;
            `}
          >
            <FormGroup label="Name" labelFor="org-name" labelInfo="(required)">
              <InputGroup
                id="org-name"
                name="name"
                defaultValue={organization.name}
                disabled={updateOrganizationSettingsMutation.isPending}
                placeholder="Enter organization name"
              />
            </FormGroup>
          </div>
          <div
            css={css`
              display: flex;
              flex-direction: rows;
              gap: 1rem;
            `}
          >
            <FormGroup
              label="Latitude"
              labelFor="org-latitude"
              labelInfo="(-90 to 90)"
            >
              <InputGroup
                id="org-latitude"
                name="latitude"
                type="number"
                min={-90}
                max={90}
                step="any"
                disabled={updateOrganizationSettingsMutation.isPending}
                defaultValue={
                  organization.location?.coordinates
                    ? String(organization.location.coordinates[1])
                    : ""
                }
                placeholder="Enter latitude"
              />
            </FormGroup>

            <FormGroup
              label="Longitude"
              labelFor="org-longitude"
              labelInfo="(-180 to 180)"
            >
              <InputGroup
                id="org-longitude"
                name="longitude"
                type="number"
                min={-180}
                max={180}
                step="any"
                disabled={updateOrganizationSettingsMutation.isPending}
                defaultValue={
                  organization.location?.coordinates
                    ? String(organization.location.coordinates[0])
                    : ""
                }
                placeholder="Enter longitude"
              />
            </FormGroup>
            <FormGroup label="Measurement Units" labelFor="org-units">
              <HTMLSelect
                id="org-units"
                name="units"
                disabled={updateOrganizationSettingsMutation.isPending}
                defaultValue={organization.units || ""}
              >
                <option value="">Select units...</option>
                <option value="metric">Metric</option>
                <option value="sae">USCS (US Customary System)</option>
              </HTMLSelect>
            </FormGroup>
            <FormGroup label="Default Basemap" labelFor="org-basemap">
              <HTMLSelect
                id="org-basemap"
                name="basemap"
                disabled={updateOrganizationSettingsMutation.isPending}
                defaultValue={organization.basemap || DEFAULT_BASEMAP}
              >
                {mapStyles.map(([key]) => (
                  <option key={key} value={key}>
                    {key}
                  </option>
                ))}
              </HTMLSelect>
            </FormGroup>
          </div>

          <StringListEditor
            label="Allowed Invite Domains"
            helperText="Folder admins may only invite email addresses at these domains. Leave empty for no restriction — organization and FYBR admins are never restricted."
            inputId="org-allowed-invite-domains"
            inputName="allowed_invite_domain_draft"
            value={allowedInviteDomains}
            onChange={setAllowedInviteDomains}
            disabled={updateOrganizationSettingsMutation.isPending}
            normalize={normalizeInviteDomain}
            validate={isValidInviteDomain}
          />
        </div>

        <Button
          text={
            updateOrganizationSettingsMutation.isPending
              ? "Saving..."
              : "Save Organization"
          }
          intent="primary"
          type="submit"
          disabled={updateOrganizationSettingsMutation.isPending}
        />
      </form>
    </Card>
  );
}

function MaterialsPanel(props: {
  orgMaterials: IMaterial[];
  handleCreateMaterial: () => void;
  handleEditMaterial: (materialId: number) => void;
  handleDisableMaterial: (materialId: number) => void;
  handleActivateMaterial?: (materialId: number) => void;
  isActivating?: boolean;
  showDeactivated: boolean;
  onShowDeactivatedChange: (checked: boolean) => void;
  canManage: boolean;
}) {
  const {
    orgMaterials,
    handleCreateMaterial,
    handleEditMaterial,
    handleDisableMaterial,
    handleActivateMaterial,
    isActivating,
    showDeactivated,
    onShowDeactivatedChange,
    canManage,
  } = props;

  return (
    <Card
      css={css`
        padding: 0;
        display: flex;
        flex-direction: column;
        height: 100%;
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 11px;
          background-color: var(--neutral-7);
          box-shadow: inset 0 0 1px 1px var(--neutral-5);
        `}
      >
        <h3>Materials</h3>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 1rem;
          `}
        >
          <Switch
            css={css`
              margin: 0;
            `}
            label="Show deactivated"
            checked={showDeactivated}
            onChange={(e) => onShowDeactivatedChange(e.currentTarget.checked)}
          />
          {canManage && (
            <Button
              icon="plus"
              text="Create Material"
              intent="primary"
              onClick={handleCreateMaterial}
            />
          )}
        </div>
      </div>
      {!canManage && (
        <ReadOnlyNotice>{readOnlyNoticeCopy.materials}</ReadOnlyNotice>
      )}
      {orgMaterials.length === 0 ? (
        <NonIdealState
          icon="unresolve"
          title="No Materials Found"
          description="No materials are assigned to this organization."
        />
      ) : (
        <>
          <div>
            <HTMLTable
              striped
              interactive
              css={css`
                width: 100%;
                border-bottom: 1px solid var(--neutral-5);
              `}
            >
              <colgroup>
                <col />
                <col width="20%" />
                <col width="30%" />
                <col width="64px" />
              </colgroup>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Inventory</th>
                  <th>Status</th>
                  <th
                    css={css`
                      width: 64px;
                    `}
                  ></th>
                </tr>
              </thead>
            </HTMLTable>
          </div>
          <div
            css={css`
              overflow-y: auto;
              & table.bp6-html-table tbody tr:first-of-type td {
                box-shadow: none;
              }
            `}
          >
            <HTMLTable
              striped
              interactive={canManage}
              css={css`
                width: 100%;
              `}
            >
              <colgroup>
                <col />
                <col width="20%" />
                <col width="30%" />
                <col width="64px" />
              </colgroup>
              <tbody>
                {orgMaterials.map((material) => (
                  <tr
                    key={material.id}
                    onClick={
                      canManage
                        ? (e) => {
                            if (!material.archived) {
                              handleEditMaterial(material.id);
                            }
                          }
                        : undefined
                    }
                    css={css`
                      cursor: ${canManage && !material.archived
                        ? "pointer"
                        : "default"};
                    `}
                  >
                    <td>
                      <div
                        css={css`
                          height: 30px;
                          display: flex;
                          align-items: center;
                          color: ${material.archived
                            ? "var(--neutral-3)"
                            : "inherit"};
                        `}
                      >
                        {material.name}
                      </div>
                    </td>
                    <td>
                      <div
                        css={css`
                          height: 30px;
                          display: flex;
                          align-items: center;
                        `}
                      >
                        <Icon
                          icon={material.inventory ? "tick" : "cross"}
                          intent={material.inventory ? "success" : "danger"}
                        />
                      </div>
                    </td>
                    <td>
                      <div
                        css={css`
                          height: 30px;
                          display: flex;
                          align-items: center;
                        `}
                      >
                        <Tag
                          intent={material.archived ? "warning" : "success"}
                          minimal
                        >
                          {material.archived ? "Deactivated" : "Active"}
                        </Tag>
                      </div>
                    </td>
                    <td>
                      <div
                        onClick={(e) => e.stopPropagation()}
                        css={css`
                          display: flex;
                          justify-content: center;
                          align-items: center;
                          height: 30px;
                          width: 100%;
                        `}
                      >
                        {canManage && (
                          <Popover
                            content={
                              <Menu>
                                {!material.archived && (
                                  <MenuItem
                                    icon="edit"
                                    text="Edit"
                                    onClick={() =>
                                      handleEditMaterial(material.id)
                                    }
                                  />
                                )}
                                {!material.archived && (
                                  <MenuItem
                                    icon="disable"
                                    text="Deactivate"
                                    intent="danger"
                                    onClick={() =>
                                      handleDisableMaterial(material.id)
                                    }
                                  />
                                )}
                                {material.archived &&
                                  handleActivateMaterial && (
                                    <MenuItem
                                      icon="tick"
                                      text={
                                        isActivating
                                          ? "Activating..."
                                          : "Activate"
                                      }
                                      onClick={() =>
                                        handleActivateMaterial(material.id)
                                      }
                                      intent="success"
                                      disabled={isActivating}
                                    />
                                  )}
                              </Menu>
                            }
                            placement="bottom-end"
                          >
                            <Button size="small" icon="more" />
                          </Popover>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </HTMLTable>
          </div>
        </>
      )}
    </Card>
  );
}

function EditOrderTypeAssignmentDialog(props: {
  isOpen: boolean;
  onClose: () => void;
  organizationId: number;
  sites: ISite[];
  orderType: IOrderType | null;
}) {
  const { isOpen, onClose, organizationId, sites, orderType } = props;

  const [selectedSiteId, setSelectedSiteId] = React.useState<number | null>(
    () => orderType?.site_id ?? null
  );
  const [deadlineHoursInput, setDeadlineHoursInput] = React.useState(() =>
    orderType?.deadline_duration_seconds != null
      ? String(roundedHoursFromSeconds(orderType.deadline_duration_seconds))
      : ""
  );
  const [items, setItems] = React.useState<string[]>(
    () => orderType?.items ?? []
  );
  const [editErrors, setEditErrors] = React.useState<string[]>([]);

  const queryClient = useQueryClient();
  const { toaster } = useOutletContext<{
    toaster: React.RefObject<OverlayToaster>;
  }>();

  const updateOrderTypeAssignmentMutation = useMutation({
    mutationFn: (payload: {
      site_id: number | null;
      deadline_duration_seconds: number;
      name: string;
      description?: string;
      items?: string[];
    }) => {
      if (!orderType?.assignment_id) {
        throw new Error("Order type assignment ID is required");
      }
      return ApiAdmin.updateOrderTypeAssignment(
        organizationId,
        orderType.assignment_id,
        payload
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["orderTypes", organizationId],
      });
      if (toaster.current) {
        toaster.current.show({
          message: "Order type assignment updated successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
      onClose();
    },
    onError: (error) => {
      if (toaster.current) {
        toaster.current.show({
          message:
            error.message ||
            "Failed to update order type assignment. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const errors: string[] = [];
    const nameInput = event.currentTarget.elements.namedItem(
      "assignment-order-type-name"
    ) as HTMLInputElement;
    const descriptionInput = event.currentTarget.elements.namedItem(
      "assignment-order-type-description"
    ) as HTMLTextAreaElement;

    const name = nameInput?.value;
    const description = descriptionInput?.value;

    if (!name || name.trim() === "") {
      errors.push("name");
    }

    const trimmed = deadlineHoursInput.trim();
    let deadline_duration_seconds: number | undefined;

    if (trimmed === "") {
      errors.push("deadline");
    } else {
      const parsed = Number(trimmed);
      if (!Number.isInteger(parsed) || parsed < 0) {
        errors.push("deadline");
      } else {
        deadline_duration_seconds = parsed * 3600;
      }
    }

    if (errors.length > 0 || deadline_duration_seconds === undefined) {
      setEditErrors(errors);
      return;
    }

    updateOrderTypeAssignmentMutation.mutate({
      site_id: selectedSiteId,
      deadline_duration_seconds,
      name: name.trim(),
      description: description?.trim() ?? "",
      items,
    });
  };

  return (
    <Dialog
      className="bp6-dark"
      css={css`
        width: 524px;
      `}
      title="Edit Order Type"
      isOpen={isOpen && orderType !== null}
      onClose={() => {
        if (!updateOrderTypeAssignmentMutation.isPending) {
          onClose();
        }
      }}
    >
      <form
        css={css`
          padding: 1rem;
        `}
        onSubmit={handleSubmit}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 1rem;
            margin-bottom: 1rem;
          `}
        >
          <FormGroup
            label="Name"
            labelFor="assignment-order-type-name"
            labelInfo="(required)"
            intent={editErrors.includes("name") ? "danger" : "none"}
          >
            <InputGroup
              id="assignment-order-type-name"
              name="assignment-order-type-name"
              intent={editErrors.includes("name") ? "danger" : "none"}
              disabled={updateOrderTypeAssignmentMutation.isPending}
              defaultValue={orderType?.name || ""}
              autoFocus
            />
          </FormGroup>

          <FormGroup
            label="Description"
            labelFor="assignment-order-type-description"
          >
            <TextArea
              id="assignment-order-type-description"
              name="assignment-order-type-description"
              disabled={updateOrderTypeAssignmentMutation.isPending}
              defaultValue={orderType?.description || ""}
              fill
              rows={12}
            />
          </FormGroup>

          <OrderTypeItemsEditor
            inputId="assignment-order-type-items"
            value={items}
            onChange={setItems}
            disabled={updateOrderTypeAssignmentMutation.isPending}
          />

          <FormGroup label="Site" labelFor="site-select">
            <HTMLSelect
              id="site-select"
              value={selectedSiteId === null ? "" : String(selectedSiteId)}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedSiteId(value === "" ? null : Number(value));
              }}
              disabled={updateOrderTypeAssignmentMutation.isPending}
            >
              <option value="">No Site</option>
              {sites.map((site) => (
                <option key={site.id} value={String(site.id)}>
                  {site.label}
                </option>
              ))}
            </HTMLSelect>
          </FormGroup>

          <FormGroup
            label="Deadline (hours)"
            labelFor="assignment-deadline-hours"
            intent={editErrors.includes("deadline") ? "danger" : "none"}
            helperText="Deadline for this order type."
          >
            <InputGroup
              id="assignment-deadline-hours"
              type="number"
              min={0}
              value={deadlineHoursInput}
              onChange={(e) => setDeadlineHoursInput(e.target.value)}
              intent={editErrors.includes("deadline") ? "danger" : "none"}
              disabled={updateOrderTypeAssignmentMutation.isPending}
            />
          </FormGroup>
        </div>

        <div
          css={css`
            display: flex;
            justify-content: flex-end;
            gap: 1rem;
            padding-top: 0.5rem;
          `}
        >
          <Button
            text="Cancel"
            disabled={updateOrderTypeAssignmentMutation.isPending}
            intent="none"
            onClick={() => {
              if (!updateOrderTypeAssignmentMutation.isPending) {
                onClose();
              }
            }}
          />
          <Button
            text={
              updateOrderTypeAssignmentMutation.isPending ? "Saving..." : "Save"
            }
            disabled={updateOrderTypeAssignmentMutation.isPending}
            intent="primary"
            type="submit"
          />
        </div>
      </form>
    </Dialog>
  );
}

function AssignChartPropertyDialog(props: {
  isOpen: boolean;
  onClose: () => void;
  organizationId: number;
}) {
  const { isOpen, onClose, organizationId } = props;
  const [layerName, setLayerName] = React.useState("");
  const [chartPropertyId, setChartPropertyId] = React.useState<number | "">("");

  React.useEffect(() => {
    if (isOpen) {
      setLayerName("");
      setChartPropertyId("");
    }
  }, [isOpen]);

  const queryClient = useQueryClient();
  const { toaster } = useOutletContext<{
    toaster: React.RefObject<OverlayToaster>;
  }>();

  const { data: chartPropsData } = useQuery({
    queryKey: ["chartProperties", "assign-dialog"],
    queryFn: () => ApiAdmin.getChartProperties(false),
    enabled: isOpen,
  });

  const assignMutation = useMutation({
    mutationFn: () =>
      ApiAdmin.assignChartPropertyToOrganization(
        organizationId,
        Number(chartPropertyId),
        layerName.trim()
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["orgChartProperties", organizationId],
      });
      toaster.current?.show({
        message: "Chart property assigned successfully.",
        intent: "success",
        timeout: 3000,
      });
      onClose();
    },
    onError: (error: Error) => {
      toaster.current?.show({
        message:
          error.message || "Failed to assign chart property. Please try again.",
        intent: "danger",
        timeout: 5000,
      });
    },
  });

  const options = React.useMemo((): IChartProperty[] => {
    return chartPropsData?.chart_properties || [];
  }, [chartPropsData?.chart_properties]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!layerName.trim() || chartPropertyId === "") {
      toaster.current?.show({
        message: "Layer name and chart property are required.",
        intent: "warning",
        timeout: 4000,
      });
      return;
    }
    assignMutation.mutate();
  };

  return (
    <Dialog
      className="bp6-dark"
      css={css`
        width: 480px;
      `}
      title="Assign Chart Property"
      isOpen={isOpen}
      onClose={() => {
        if (!assignMutation.isPending) onClose();
      }}
    >
      <form
        css={css`
          padding: 1rem;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        `}
        onSubmit={handleSubmit}
      >
        <FormGroup label="Layer name" labelFor="chart-prop-layer">
          <InputGroup
            id="chart-prop-layer"
            value={layerName}
            onChange={(e) => setLayerName(e.target.value)}
            disabled={assignMutation.isPending}
            placeholder="e.g. Piles"
          />
        </FormGroup>
        <FormGroup label="Chart property" labelFor="chart-prop-select">
          <HTMLSelect
            id="chart-prop-select"
            value={chartPropertyId === "" ? "" : String(chartPropertyId)}
            onChange={(e) => {
              const v = e.target.value;
              setChartPropertyId(v === "" ? "" : Number(v));
            }}
            disabled={assignMutation.isPending}
          >
            <option value="">Select…</option>
            {options.map((cp) => (
              <option key={cp.id} value={String(cp.id)}>
                {cp.label}
              </option>
            ))}
          </HTMLSelect>
        </FormGroup>
        <div
          css={css`
            display: flex;
            justify-content: flex-end;
            gap: 1rem;
          `}
        >
          <Button
            text="Cancel"
            intent="none"
            disabled={assignMutation.isPending}
            onClick={() => !assignMutation.isPending && onClose()}
          />
          <Button
            text={assignMutation.isPending ? "Saving…" : "Assign"}
            intent="primary"
            type="submit"
            disabled={assignMutation.isPending}
          />
        </div>
      </form>
    </Dialog>
  );
}

function AssignOrderTypeDialog(props: {
  isOpen: boolean;
  onClose: () => void;
  organizationId: number;
  sites: ISite[];
  assignedOrderTypes: Array<{ order_type_id: number; site_id: number | null }>;
}) {
  const { isOpen, onClose, organizationId, sites, assignedOrderTypes } = props;

  const [selectedSiteId, setSelectedSiteId] = React.useState<number | null>(
    null
  );

  // Reset site selection when dialog opens
  React.useEffect(() => {
    if (isOpen) {
      setSelectedSiteId(null);
    }
  }, [isOpen]);

  const queryClient = useQueryClient();
  const { toaster } = useOutletContext<{
    toaster: React.RefObject<OverlayToaster>;
  }>();

  const { data: allOrderTypesData } = useQuery({
    queryKey: ["allOrderTypes"],
    queryFn: () => ApiAdmin.getAllOrderTypes(),
  });

  const assignOrderTypeMutation = useMutation({
    mutationFn: ({
      orderTypeId,
      siteId,
    }: {
      orderTypeId: number;
      siteId: number | null;
    }) =>
      ApiAdmin.assignOrderTypeToOrganization(
        organizationId,
        orderTypeId,
        siteId
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["orderTypes", organizationId],
      });
      if (toaster.current) {
        toaster.current.show({
          message: "Order type added successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
    },
    onError: (error) => {
      if (toaster.current) {
        toaster.current.show({
          message:
            error.message || "Failed to add order type. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
  });

  const availableOrderTypes = React.useMemo(() => {
    if (!allOrderTypesData?.order_types) return [];
    return allOrderTypesData.order_types.filter((ot) => !ot.deleted_at);
  }, [allOrderTypesData?.order_types]);

  const isOrderTypeAssigned = (orderTypeId: number) => {
    return assignedOrderTypes.some(
      (assigned) =>
        assigned.order_type_id === orderTypeId &&
        (selectedSiteId === null
          ? assigned.site_id === null
          : assigned.site_id === selectedSiteId)
    );
  };

  const handleSelectOrderType = (orderTypeId: number) => {
    assignOrderTypeMutation.mutate({
      orderTypeId,
      siteId: selectedSiteId,
    });
  };

  return (
    <Dialog
      className="bp6-dark"
      css={css`
        width: 524px;
      `}
      title="Add Order Type"
      isOpen={isOpen}
      onClose={() => {
        if (!assignOrderTypeMutation.isPending) {
          onClose();
        }
      }}
    >
      <div
        css={css`
          padding: 1rem;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        `}
      >
        <FormGroup label="Site" labelFor="site-select">
          <HTMLSelect
            id="site-select"
            value={selectedSiteId === null ? "" : String(selectedSiteId)}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedSiteId(value === "" ? null : Number(value));
            }}
            disabled={assignOrderTypeMutation.isPending}
          >
            <option value="">No Site</option>
            {sites.map((site) => (
              <option key={site.id} value={String(site.id)}>
                {site.label}
              </option>
            ))}
          </HTMLSelect>
        </FormGroup>
        {availableOrderTypes.length === 0 ? (
          <NonIdealState
            icon="cube"
            title="No Order Templates Available"
            description="No order templates are available to assign."
          />
        ) : (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 0.5rem;
              max-height: 400px;
              overflow-y: auto;
            `}
          >
            {availableOrderTypes.map((orderType) => {
              const isAssigned = isOrderTypeAssigned(orderType.id);
              return (
                <Button
                  key={orderType.id}
                  text={orderType.name}
                  endIcon={isAssigned ? "tick" : "add"}
                  fill
                  alignText="left"
                  disabled={assignOrderTypeMutation.isPending || isAssigned}
                  onClick={() => handleSelectOrderType(orderType.id)}
                  css={css`
                    justify-content: space-between;
                  `}
                />
              );
            })}
          </div>
        )}
      </div>
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          padding: 0.5rem 1rem;
          border-top: 1px solid var(--neutral-5);
        `}
      >
        <Button
          text="Close"
          intent="none"
          onClick={onClose}
          disabled={assignOrderTypeMutation.isPending}
        />
      </div>
    </Dialog>
  );
}

function SitesPanel(props: {
  orgSites: ISite[];
  handleCreateSite: () => void;
  handleEditSite: (siteId: number) => void;
  handleRemoveSite: (siteId: number) => void;
  handleActivateSite?: (siteId: number) => void;
  isActivating?: boolean;
  showDeactivated: boolean;
  onShowDeactivatedChange: (checked: boolean) => void;
  canManage: boolean;
}) {
  const {
    orgSites,
    handleCreateSite,
    handleEditSite,
    handleRemoveSite,
    handleActivateSite,
    isActivating,
    showDeactivated,
    onShowDeactivatedChange,
    canManage,
  } = props;

  const isSiteDeactivated = (site: ISite) => {
    return site.deleted_at !== null && site.deleted_at !== undefined;
  };

  return (
    <Card
      css={css`
        padding: 0;
        display: flex;
        flex-direction: column;
        height: 100%;
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 11px;
          background-color: var(--neutral-7);
          box-shadow: inset 0 0 1px 1px var(--neutral-5);
        `}
      >
        <h3>Sites</h3>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 1rem;
          `}
        >
          <Switch
            css={css`
              margin: 0;
            `}
            label="Show deactivated"
            checked={showDeactivated}
            onChange={(e) => onShowDeactivatedChange(e.currentTarget.checked)}
          />
          {canManage && (
            <Button
              icon="plus"
              text="Create Site"
              intent="primary"
              onClick={handleCreateSite}
            />
          )}
        </div>
      </div>
      {!canManage && (
        <ReadOnlyNotice>{readOnlyNoticeCopy.sites}</ReadOnlyNotice>
      )}
      {orgSites.length === 0 ? (
        <NonIdealState
          icon="map-marker"
          title="No Sites Found"
          description="No sites are assigned to this organization."
        />
      ) : (
        <>
          <div>
            <HTMLTable
              striped
              interactive
              css={css`
                width: 100%;
                border-bottom: 1px solid var(--neutral-5);
              `}
            >
              <colgroup>
                <col />
                <col width="15%" />
                <col width="15%" />
                <col width="30%" />
                <col width="64px" />
              </colgroup>
              <thead>
                <tr>
                  <th>Label</th>
                  <th>Latitude</th>
                  <th>Longitude</th>
                  <th>Status</th>
                  <th
                    css={css`
                      width: 64px;
                    `}
                  ></th>
                </tr>
              </thead>
            </HTMLTable>
          </div>
          <div
            css={css`
              overflow-y: auto;
              & table.bp6-html-table tbody tr:first-of-type td {
                box-shadow: none;
              }
            `}
          >
            <HTMLTable
              striped
              interactive={canManage}
              css={css`
                width: 100%;
              `}
            >
              <colgroup>
                <col />
                <col width="15%" />
                <col width="15%" />
                <col width="30%" />
                <col width="64px" />
              </colgroup>
              <tbody>
                {orgSites.map((site) => {
                  const isDeactivated = isSiteDeactivated(site);
                  const latitude = site.location?.coordinates?.[1];
                  const longitude = site.location?.coordinates?.[0];
                  return (
                    <tr
                      key={site.id}
                      onClick={
                        canManage
                          ? (e) => {
                              if (!isDeactivated) {
                                handleEditSite(site.id);
                              }
                            }
                          : undefined
                      }
                      css={css`
                        cursor: ${canManage && !isDeactivated
                          ? "pointer"
                          : "default"};
                      `}
                    >
                      <td>
                        <div
                          css={css`
                            height: 30px;
                            display: flex;
                            align-items: center;
                            color: ${isDeactivated
                              ? "var(--neutral-3)"
                              : "inherit"};
                          `}
                        >
                          {site.label}
                        </div>
                      </td>
                      <td>
                        <div
                          css={css`
                            height: 30px;
                            display: flex;
                            align-items: center;
                            color: ${isDeactivated
                              ? "var(--neutral-3)"
                              : "inherit"};
                          `}
                        >
                          {latitude !== undefined ? latitude.toFixed(6) : "—"}
                        </div>
                      </td>
                      <td>
                        <div
                          css={css`
                            height: 30px;
                            display: flex;
                            align-items: center;
                            color: ${isDeactivated
                              ? "var(--neutral-3)"
                              : "inherit"};
                          `}
                        >
                          {longitude !== undefined ? longitude.toFixed(6) : "—"}
                        </div>
                      </td>
                      <td>
                        <div
                          css={css`
                            height: 30px;
                            display: flex;
                            align-items: center;
                          `}
                        >
                          <Tag
                            intent={isDeactivated ? "warning" : "success"}
                            minimal
                          >
                            {isDeactivated ? "Deactivated" : "Active"}
                          </Tag>
                        </div>
                      </td>
                      <td>
                        <div
                          onClick={(e) => e.stopPropagation()}
                          css={css`
                            display: flex;
                            justify-content: center;
                            align-items: center;
                            height: 30px;
                            width: 100%;
                          `}
                        >
                          {canManage && (
                            <Popover
                              content={
                                <Menu>
                                  {!isDeactivated && (
                                    <>
                                      <MenuItem
                                        icon="edit"
                                        text="Edit"
                                        onClick={() => handleEditSite(site.id)}
                                      />
                                      <MenuItem
                                        icon="disable"
                                        text="Deactivate"
                                        intent="danger"
                                        onClick={() =>
                                          handleRemoveSite(site.id)
                                        }
                                      />
                                    </>
                                  )}
                                  {isDeactivated && handleActivateSite && (
                                    <MenuItem
                                      icon="tick"
                                      text={
                                        isActivating
                                          ? "Activating..."
                                          : "Activate"
                                      }
                                      onClick={() =>
                                        handleActivateSite(site.id)
                                      }
                                      intent="success"
                                      disabled={isActivating}
                                    />
                                  )}
                                </Menu>
                              }
                              placement="bottom-end"
                            >
                              <Button size="small" icon="more" />
                            </Popover>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </HTMLTable>
          </div>
        </>
      )}
    </Card>
  );
}

export function OrganizationSettingsPage(props: {
  organizationId: number;
  /** FYBR admins manage materials, sites, and order type assignments; org admins see them read-only.
   *  Chart property assignments and the General settings tab stay editable for org admins. */
  canManageProvisionedResources: boolean;
}) {
  const { organizationId, canManageProvisionedResources } = props;

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { toaster } = useOutletContext<{
    toaster: React.RefObject<OverlayToaster>;
  }>();

  const {
    data: organizationData,
    isLoading: orgLoading,
    error: orgError,
  } = useQuery({
    queryKey: ["organization", organizationId],
    queryFn: () => ApiAdmin.getOrganization(organizationId),
    enabled: !isNaN(organizationId),
  });

  const { data: materialsData } = useQuery({
    queryKey: ["materials", organizationId],
    queryFn: () => ApiAdmin.getMaterialsForOrganization(organizationId),
    enabled: !isNaN(organizationId),
  });

  const { data: sitesData } = useQuery({
    queryKey: ["sites", organizationId],
    queryFn: () => ApiAdmin.getSitesForOrganization(organizationId, true),
    enabled: !isNaN(organizationId),
  });

  const { data: orderTypesData } = useQuery({
    queryKey: ["orderTypes", organizationId],
    queryFn: () => ApiAdmin.getOrderTypesForOrganization(organizationId, true),
    enabled: !isNaN(organizationId),
  });

  const { data: orgChartPropsData } = useQuery({
    queryKey: ["orgChartProperties", organizationId],
    queryFn: () =>
      ApiAdmin.getChartPropertiesForOrganization(organizationId, true),
    enabled: !isNaN(organizationId),
  });

  const organization = organizationData?.organization;
  const materials = materialsData?.materials || [];
  const [showDeactivatedMaterials, setShowDeactivatedMaterials] =
    React.useState(false);
  const [showDeactivatedSites, setShowDeactivatedSites] = React.useState(false);
  const [showDeactivatedOrderTypes, setShowDeactivatedOrderTypes] =
    React.useState(false);
  const [showDeactivatedChartProps, setShowDeactivatedChartProps] =
    React.useState(false);

  const orgSites = React.useMemo(() => {
    if (!sitesData?.sites) return [];
    return sitesData.sites
      .filter((site) => {
        if (!showDeactivatedSites) {
          return site.deleted_at === null || site.deleted_at === undefined;
        }
        return true;
      })
      .sort((a, b) => {
        const aActive = !a.deleted_at;
        const bActive = !b.deleted_at;
        if (aActive !== bActive) return aActive ? -1 : 1;
        return a.label.localeCompare(b.label);
      });
  }, [sitesData?.sites, showDeactivatedSites]);

  const orgMaterials = React.useMemo(() => {
    if (!materials || !organization) return [];
    return materials
      .filter((m) => m.organization_id === organization.id)
      .filter((m) => {
        if (!showDeactivatedMaterials) {
          return !m.archived || m.archived === 0;
        }
        return true;
      })
      .sort((a, b) => {
        const aActive = !a.archived;
        const bActive = !b.archived;
        if (aActive !== bActive) return aActive ? -1 : 1;
        return a.name.localeCompare(b.name);
      });
  }, [materials, organization, showDeactivatedMaterials]);

  const orgOrderTypes = React.useMemo(() => {
    if (!orderTypesData?.order_types) return [];
    return orderTypesData.order_types
      .filter((ot) => {
        // Filter out order types without assignment_id
        if (ot.assignment_id === undefined) return false;
        if (!showDeactivatedOrderTypes) {
          return ot.deleted_at === null || ot.deleted_at === undefined;
        }
        return true;
      })
      .sort((a, b) => {
        const aActive = !a.deleted_at;
        const bActive = !b.deleted_at;
        if (aActive !== bActive) return aActive ? -1 : 1;
        return a.name.localeCompare(b.name);
      });
  }, [orderTypesData?.order_types, showDeactivatedOrderTypes]);

  const orgChartPropertyAssignments = orgChartPropsData?.chart_properties ?? [];

  const [editMaterialDialog, setEditMaterialDialog] =
    React.useState<IMaterial | null>(null);
  const [createMaterialDialogOpen, setCreateMaterialDialogOpen] =
    React.useState(false);
  const [disableMaterialDialog, setDisableMaterialDialog] =
    React.useState<IMaterial | null>(null);
  const [editSiteDialog, setEditSiteDialog] = React.useState<ISite | null>(
    null
  );
  const [createSiteDialogOpen, setCreateSiteDialogOpen] = React.useState(false);
  const [deactivateSiteDialog, setDeactivateSiteDialog] =
    React.useState<ISite | null>(null);
  const [assignOrderTypeDialogOpen, setAssignOrderTypeDialogOpen] =
    React.useState(false);
  const [assignChartPropertyDialogOpen, setAssignChartPropertyDialogOpen] =
    React.useState(false);
  const [deactivateOrderTypeDialog, setDeactivateOrderTypeDialog] =
    React.useState<IOrderType | null>(null);
  const [deactivateChartPropDialog, setDeactivateChartPropDialog] =
    React.useState<IOrgChartPropertyAssignment | null>(null);
  const [editOrderTypeAssignmentDialog, setEditOrderTypeAssignmentDialog] =
    React.useState<IOrderType | null>(null);
  const [selectedTabId, setSelectedTabId] = React.useState<string>(() =>
    resolveOrganizationEditTab(searchParams.get("tab"))
  );
  const tabParam = searchParams.get("tab");

  React.useEffect(() => {
    const next = resolveOrganizationEditTab(tabParam);
    setSelectedTabId((prev) => (prev === next ? prev : next));
  }, [tabParam]);

  const updateOrganizationSettingsMutation = useMutation({
    mutationFn: (data: {
      name?: string;
      units?: "metric" | "sae";
      basemap?: string;
      location?: { latitude: number; longitude: number };
      allowed_invite_domains?: string[];
    }) => ApiAdmin.updateOrganization(organizationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["organization", organizationId],
      });
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      if (toaster.current) {
        toaster.current.show({
          message: "Organization updated successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
    },
    onError: (error) => {
      if (toaster.current) {
        toaster.current.show({
          message:
            error.message || "Failed to update organization. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
  });

  const updateOrganizationMutation = useMutation({
    mutationFn: (data: Partial<{ site_ids: number[] }>) =>
      ApiAdmin.updateOrganization(organizationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["organization", organizationId],
      });
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      queryClient.invalidateQueries({
        queryKey: ["sites", organizationId],
      });
      if (toaster.current) {
        toaster.current.show({
          message: "Organization updated successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
    },
    onError: (error) => {
      if (toaster.current) {
        toaster.current.show({
          message:
            error.message || "Failed to update organization. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
  });

  const handleOrganizationSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
    allowedInviteDomains: string[]
  ) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const name = formData.get("name") as string;
    const units = formData.get("units") as string;
    const latitudeStr = formData.get("latitude") as string;
    const longitudeStr = formData.get("longitude") as string;
    const basemapRaw = formData.get("basemap") as string;

    const errors: string[] = [];
    const allowedBasemapKeys = new Set(mapStyles.map(([key]) => key));

    // Validate name
    let nameValue: string | undefined;
    if (!name || name.trim() === "") {
      errors.push("name is required");
    } else {
      nameValue = name.trim();
    }

    // Validate units
    let unitsValue: "metric" | "sae" | undefined;
    if (units && units !== "") {
      if (units !== "metric" && units !== "sae") {
        errors.push("units must be 'metric' or 'sae'");
      } else {
        unitsValue = units as "metric" | "sae";
      }
    }

    if (typeof basemapRaw !== "string" || !allowedBasemapKeys.has(basemapRaw)) {
      errors.push("basemap must be a valid map style");
    }

    // Validate and parse location
    let locationValue: { latitude: number; longitude: number } | undefined;
    const hasLatitude = latitudeStr && latitudeStr.trim() !== "";
    const hasLongitude = longitudeStr && longitudeStr.trim() !== "";

    if (hasLatitude || hasLongitude) {
      if (!hasLatitude || !hasLongitude) {
        errors.push(
          "both latitude and longitude are required if location is provided"
        );
      } else {
        const latitude = parseFloat(latitudeStr);
        const longitude = parseFloat(longitudeStr);

        if (isNaN(latitude) || isNaN(longitude)) {
          errors.push("latitude and longitude must be valid numbers");
        } else {
          if (latitude < -90 || latitude > 90) {
            errors.push("latitude must be between -90 and 90");
          }
          if (longitude < -180 || longitude > 180) {
            errors.push("longitude must be between -180 and 180");
          }

          if (errors.length === 0) {
            locationValue = { latitude, longitude };
          }
        }
      }
    }

    const draftMerge = mergePendingDomainDraft(
      allowedInviteDomains,
      formData.get("allowed_invite_domain_draft") as string | null
    );
    if (draftMerge.status === "invalid") {
      errors.push(draftMerge.error);
    }

    if (errors.length > 0) {
      if (toaster.current) {
        toaster.current.show({
          message: errors.join(". "),
          intent: "danger",
          timeout: 5000,
        });
      }
      return;
    }

    const updateData: {
      name?: string;
      units?: "metric" | "sae";
      basemap?: string;
      location?: { latitude: number; longitude: number };
      allowed_invite_domains?: string[];
    } = {};

    if (nameValue !== undefined && nameValue !== organization.name) {
      updateData.name = nameValue;
    }

    if (unitsValue !== undefined && unitsValue !== organization.units) {
      updateData.units = unitsValue;
    }

    if (basemapRaw !== (organization.basemap || DEFAULT_BASEMAP)) {
      updateData.basemap = basemapRaw;
    }

    const priorCoords = organization.location?.coordinates;
    let priorLocation: { latitude: number; longitude: number } | undefined;
    if (
      priorCoords &&
      priorCoords.length >= 2 &&
      typeof priorCoords[0] === "number" &&
      typeof priorCoords[1] === "number"
    ) {
      priorLocation = {
        latitude: priorCoords[1],
        longitude: priorCoords[0],
      };
    }
    if (
      locationValue !== undefined &&
      (priorLocation === undefined ||
        locationValue.latitude !== priorLocation.latitude ||
        locationValue.longitude !== priorLocation.longitude)
    ) {
      updateData.location = locationValue;
    }

    const normalizedDomains =
      draftMerge.status === "ok" ? draftMerge.domains : [];
    const priorDomains = organization.allowed_invite_domains ?? [];
    const domainsChanged =
      normalizedDomains.length !== priorDomains.length ||
      normalizedDomains.some((domain, i) => domain !== priorDomains[i]);
    if (domainsChanged) {
      updateData.allowed_invite_domains = normalizedDomains;
    }

    if (Object.keys(updateData).length > 0) {
      updateOrganizationSettingsMutation.mutate(updateData);
    } else if (toaster.current) {
      toaster.current.show({
        message: "No changes to save.",
        intent: "none",
        timeout: 3000,
      });
    }
  };

  const handleEditMaterial = (materialId: number) => {
    const material = materials.find((m) => m.id === materialId);
    if (material) {
      setEditMaterialDialog(material);
    }
  };

  const handleCreateMaterial = () => {
    setCreateMaterialDialogOpen(true);
  };

  const handleDisableMaterial = (materialId: number) => {
    const material = materials.find((m) => m.id === materialId);
    if (material) {
      setDisableMaterialDialog(material);
    }
  };

  const activateMaterialMutation = useMutation({
    mutationFn: (materialId: number) =>
      ApiAdmin.updateMaterial(organizationId, materialId, {
        archived: 0,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["materials"] });
      if (toaster.current) {
        toaster.current.show({
          message: "Material activated successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
    },
    onError: (error) => {
      if (toaster.current) {
        toaster.current.show({
          message:
            error.message || "Failed to activate material. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
  });

  const handleActivateMaterial = (materialId: number) => {
    activateMaterialMutation.mutate(materialId);
  };

  const handleEditSite = (siteId: number) => {
    const site = orgSites.find((s) => s.id === siteId);
    if (site) {
      setEditSiteDialog(site);
    }
  };

  const handleCreateSite = () => {
    setCreateSiteDialogOpen(true);
  };

  const handleRemoveSite = (siteId: number) => {
    const site = orgSites.find((s) => s.id === siteId);
    if (site) {
      setDeactivateSiteDialog(site);
    }
  };

  const handleConfirmDeactivateSite = () => {
    if (deactivateSiteDialog) {
      const newSiteIds =
        organization.site_ids?.filter((id) => id !== deactivateSiteDialog.id) ||
        [];
      updateOrganizationMutation.mutate({ site_ids: newSiteIds });
      setDeactivateSiteDialog(null);
    }
  };

  const activateSiteMutation = useMutation({
    mutationFn: (siteId: number) =>
      ApiAdmin.activateSite(organizationId, siteId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["sites", organizationId],
      });
      queryClient.invalidateQueries({
        queryKey: ["organization", organizationId],
      });
      if (toaster.current) {
        toaster.current.show({
          message: "Site activated successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
    },
    onError: (error) => {
      if (toaster.current) {
        toaster.current.show({
          message:
            error.message || "Failed to activate site. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
  });

  const handleActivateSite = (siteId: number) => {
    activateSiteMutation.mutate(siteId);
  };

  const assignOrderTypeMutation = useMutation({
    mutationFn: (orderTypeId: number) =>
      ApiAdmin.assignOrderTypeToOrganization(organizationId, orderTypeId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["orderTypes", organizationId],
      });
      if (toaster.current) {
        toaster.current.show({
          message: "Order type assigned successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
      setAssignOrderTypeDialogOpen(false);
    },
    onError: (error) => {
      if (toaster.current) {
        toaster.current.show({
          message:
            error.message || "Failed to assign order type. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
  });

  const deactivateOrderTypeMutation = useMutation({
    mutationFn: (assignmentId: number) =>
      ApiAdmin.deactivateOrderTypeAssignment(organizationId, assignmentId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["orderTypes", organizationId],
      });
      if (toaster.current) {
        const isDeleted = data?.action === "deleted";
        toaster.current.show({
          message: isDeleted
            ? "Order type assignment deleted successfully."
            : "Order type assignment deactivated successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
      setDeactivateOrderTypeDialog(null);
    },
    onError: (error) => {
      if (toaster.current) {
        toaster.current.show({
          message:
            error.message ||
            "Failed to deactivate order type. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
  });

  const activateOrderTypeMutation = useMutation({
    mutationFn: (assignmentId: number) =>
      ApiAdmin.activateOrderTypeAssignment(organizationId, assignmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["orderTypes", organizationId],
      });
      if (toaster.current) {
        toaster.current.show({
          message: "Order type activated successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
    },
    onError: (error) => {
      if (toaster.current) {
        toaster.current.show({
          message:
            error.message || "Failed to activate order type. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
  });

  const handleAssignOrderType = () => {
    setAssignOrderTypeDialogOpen(true);
  };

  const handleDeactivateOrderType = (assignmentId: number) => {
    const orderType = orgOrderTypes.find(
      (ot) => ot.assignment_id === assignmentId
    );
    if (orderType) {
      setDeactivateOrderTypeDialog(orderType);
    }
  };

  const handleActivateOrderType = (assignmentId: number) => {
    activateOrderTypeMutation.mutate(assignmentId);
  };

  const handleEditOrderTypeAssignment = (assignmentId: number) => {
    const orderType = orgOrderTypes.find(
      (ot) => ot.assignment_id === assignmentId
    );
    if (orderType) {
      setEditOrderTypeAssignmentDialog(orderType);
    }
  };

  const removeChartPropertyAssignmentMutation = useMutation({
    mutationFn: (assignmentId: number) =>
      ApiAdmin.removeChartPropertyAssignment(organizationId, assignmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["orgChartProperties", organizationId],
      });
      toaster.current?.show({
        message: "Chart property assignment deactivated.",
        intent: "success",
        timeout: 3000,
      });
      setDeactivateChartPropDialog(null);
    },
    onError: (error: Error) => {
      toaster.current?.show({
        message:
          error.message ||
          "Failed to remove chart property assignment. Please try again.",
        intent: "danger",
        timeout: 5000,
      });
    },
  });

  const activateChartPropertyAssignmentMutation = useMutation({
    mutationFn: (assignmentId: number) =>
      ApiAdmin.activateChartPropertyAssignment(organizationId, assignmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["orgChartProperties", organizationId],
      });
      toaster.current?.show({
        message: "Chart property assignment reactivated.",
        intent: "success",
        timeout: 3000,
      });
    },
    onError: (error: Error) => {
      toaster.current?.show({
        message:
          error.message ||
          "Failed to reactivate chart property assignment. Please try again.",
        intent: "danger",
        timeout: 5000,
      });
    },
  });

  const handleAssignChartProperty = () => {
    setAssignChartPropertyDialogOpen(true);
  };

  const handleRemoveChartPropertyAssignment = (assignmentId: number) => {
    const row = orgChartPropertyAssignments.find(
      (a) => a.assignment_id === assignmentId
    );
    if (row) setDeactivateChartPropDialog(row);
  };

  const handleActivateChartPropertyAssignment = (assignmentId: number) => {
    activateChartPropertyAssignmentMutation.mutate(assignmentId);
  };

  if (isNaN(organizationId)) {
    return (
      <NonIdealState
        icon="error"
        title="Invalid Organization ID"
        description="The organization ID is not valid."
      />
    );
  }

  if (orgLoading) {
    return (
      <div
        css={css`
          display: flex;
          justify-content: center;
          align-items: center;
          height: 200px;
        `}
      >
        <Spinner />
      </div>
    );
  }

  if (orgError || !organization) {
    return (
      <NonIdealState
        icon="error"
        title="Error Loading Organization"
        description={orgError?.message || "Organization not found."}
      />
    );
  }

  return (
    <div
      css={css`
        padding: 2rem;
        max-width: 1440px;
        margin: 0 auto 0 0;
        display: flex;
        flex-direction: column;
        height: 100%;
      `}
    >
      <h1>{organization.name}</h1>
      <Tabs
        id="organization-edit-tabs"
        selectedTabId={selectedTabId}
        onChange={(newTabId) => {
          const tabId = newTabId as string;
          setSelectedTabId(tabId);
          navigate(`${location.pathname}?tab=${tabId}`, {
            replace: true,
          });
        }}
        size="large"
      >
        <Tab id="settings" title="Settings" icon="settings" />
        <Tab id="materials" title="Materials" icon="unresolve" />
        <Tab id="sites" title="Sites" icon="map-marker" />
        <Tab id="orderTypes" title="Order Types" icon="cube" />
        <Tab id="chartProperties" title="Chart Properties" icon="chart" />
      </Tabs>
      <div
        css={css`
          flex: 1;
          margin-top: 1rem;
          min-height: 0;
          display: flex;
          flex-direction: column;
        `}
      >
        <TabPanel
          id="settings"
          selectedTabId={selectedTabId}
          parentId="organization-edit-tabs"
          css={css`
            margin: 0;
            height: 100%;
          `}
          panel={
            <SettingsPanel
              key={`${organizationId}:${(organization.allowed_invite_domains ?? []).join(",")}`}
              organization={organization}
              updateOrganizationSettingsMutation={
                updateOrganizationSettingsMutation
              }
              handleOrganizationSubmit={handleOrganizationSubmit}
            />
          }
        />
        <TabPanel
          id="materials"
          selectedTabId={selectedTabId}
          parentId="organization-edit-tabs"
          css={css`
            margin: 0;
            height: 100%;
          `}
          panel={
            <MaterialsPanel
              orgMaterials={orgMaterials}
              handleCreateMaterial={handleCreateMaterial}
              handleEditMaterial={handleEditMaterial}
              handleDisableMaterial={handleDisableMaterial}
              handleActivateMaterial={handleActivateMaterial}
              isActivating={activateMaterialMutation.isPending}
              showDeactivated={showDeactivatedMaterials}
              onShowDeactivatedChange={setShowDeactivatedMaterials}
              canManage={canManageProvisionedResources}
            />
          }
        />
        <TabPanel
          id="sites"
          selectedTabId={selectedTabId}
          parentId="organization-edit-tabs"
          css={css`
            margin: 0;
            height: 100%;
          `}
          panel={
            <SitesPanel
              orgSites={orgSites}
              handleCreateSite={handleCreateSite}
              handleEditSite={handleEditSite}
              handleRemoveSite={handleRemoveSite}
              handleActivateSite={handleActivateSite}
              isActivating={activateSiteMutation.isPending}
              showDeactivated={showDeactivatedSites}
              onShowDeactivatedChange={setShowDeactivatedSites}
              canManage={canManageProvisionedResources}
            />
          }
        />
        <TabPanel
          id="orderTypes"
          selectedTabId={selectedTabId}
          parentId="organization-edit-tabs"
          css={css`
            margin: 0;
            flex: 1;
            min-height: 0;
            display: flex;
            flex-direction: column;
          `}
          panel={
            <OrderTypesPanel
              orgOrderTypes={orgOrderTypes}
              handleAssignOrderType={handleAssignOrderType}
              handleDeactivateOrderType={handleDeactivateOrderType}
              handleActivateOrderType={handleActivateOrderType}
              handleEditOrderTypeAssignment={handleEditOrderTypeAssignment}
              isActivating={activateOrderTypeMutation.isPending}
              showDeactivated={showDeactivatedOrderTypes}
              onShowDeactivatedChange={setShowDeactivatedOrderTypes}
              canManage={canManageProvisionedResources}
            />
          }
        />
        <TabPanel
          id="chartProperties"
          selectedTabId={selectedTabId}
          parentId="organization-edit-tabs"
          css={css`
            margin: 0;
            flex: 1;
            min-height: 0;
            display: flex;
            flex-direction: column;
          `}
          panel={
            <ChartPropertiesPanel
              assignments={orgChartPropertyAssignments}
              handleAssign={handleAssignChartProperty}
              handleRemove={handleRemoveChartPropertyAssignment}
              handleActivate={handleActivateChartPropertyAssignment}
              showDeactivated={showDeactivatedChartProps}
              onShowDeactivatedChange={setShowDeactivatedChartProps}
            />
          }
        />
      </div>

      {canManageProvisionedResources && (
        <>
          <MaterialEditDialog
            editMaterial={editMaterialDialog}
            isCreate={createMaterialDialogOpen}
            organizationId={organizationId}
            onClose={() => {
              setEditMaterialDialog(null);
              setCreateMaterialDialogOpen(false);
            }}
          />
          <DisableMaterialDialog
            material={disableMaterialDialog}
            organizationId={organizationId}
            onClose={() => setDisableMaterialDialog(null)}
          />
          <SiteEditDialog
            editSite={editSiteDialog}
            isCreate={createSiteDialogOpen}
            organizationId={organizationId}
            onClose={() => {
              setEditSiteDialog(null);
              setCreateSiteDialogOpen(false);
            }}
          />
          <ConfirmationDialog
            entityName={deactivateSiteDialog?.label || ""}
            entityType="Site"
            message="This site will be deactivated for this organization."
            isOpen={deactivateSiteDialog !== null}
            onClose={() => setDeactivateSiteDialog(null)}
            onConfirm={handleConfirmDeactivateSite}
            isPending={updateOrganizationMutation.isPending}
            actionText="Deactivate"
            actioningText="Deactivating..."
            titleAction="Deactivate"
            icon="disable"
          />
          <AssignOrderTypeDialog
            isOpen={assignOrderTypeDialogOpen}
            organizationId={organizationId}
            sites={orgSites.filter(
              (site) =>
                site.deleted_at === null || site.deleted_at === undefined
            )}
            assignedOrderTypes={orgOrderTypes.map((ot) => ({
              order_type_id: ot.id,
              site_id: ot.site_id ?? null,
            }))}
            onClose={() => setAssignOrderTypeDialogOpen(false)}
          />
          <EditOrderTypeAssignmentDialog
            key={editOrderTypeAssignmentDialog?.assignment_id ?? "closed"}
            isOpen={editOrderTypeAssignmentDialog !== null}
            organizationId={organizationId}
            sites={orgSites.filter(
              (site) =>
                site.deleted_at === null || site.deleted_at === undefined
            )}
            orderType={editOrderTypeAssignmentDialog}
            onClose={() => setEditOrderTypeAssignmentDialog(null)}
          />
          <ConfirmationDialog
            entityName={
              deactivateOrderTypeDialog
                ? `${deactivateOrderTypeDialog.name}${
                    deactivateOrderTypeDialog.site_label
                      ? ` for ${deactivateOrderTypeDialog.site_label}`
                      : ""
                  }`
                : ""
            }
            entityType="Order Type"
            message={
              deactivateOrderTypeDialog &&
              (deactivateOrderTypeDialog.order_count || 0) === 0
                ? "This order type assignment will be permanently deleted from this organization."
                : "This order type will be deactivated for this organization."
            }
            isOpen={deactivateOrderTypeDialog !== null}
            onClose={() => setDeactivateOrderTypeDialog(null)}
            onConfirm={() =>
              deactivateOrderTypeDialog &&
              deactivateOrderTypeDialog.assignment_id &&
              deactivateOrderTypeMutation.mutate(
                deactivateOrderTypeDialog.assignment_id
              )
            }
            isPending={deactivateOrderTypeMutation.isPending}
            actionText={
              deactivateOrderTypeDialog &&
              (deactivateOrderTypeDialog.order_count || 0) === 0
                ? "Delete"
                : "Deactivate"
            }
            actioningText={
              deactivateOrderTypeDialog &&
              (deactivateOrderTypeDialog.order_count || 0) === 0
                ? "Deleting..."
                : "Deactivating..."
            }
            titleAction={
              deactivateOrderTypeDialog &&
              (deactivateOrderTypeDialog.order_count || 0) === 0
                ? "Delete"
                : "Deactivate"
            }
            icon="disable"
          />
        </>
      )}
      <AssignChartPropertyDialog
        isOpen={assignChartPropertyDialogOpen}
        organizationId={organizationId}
        onClose={() => setAssignChartPropertyDialogOpen(false)}
      />
      <ConfirmationDialog
        entityName={
          deactivateChartPropDialog
            ? `${deactivateChartPropDialog.label} — ${deactivateChartPropDialog.layer_name}`
            : ""
        }
        entityType="Chart property assignment"
        message="This assignment will be deactivated for the organization."
        isOpen={deactivateChartPropDialog !== null}
        onClose={() => setDeactivateChartPropDialog(null)}
        onConfirm={() =>
          deactivateChartPropDialog &&
          removeChartPropertyAssignmentMutation.mutate(
            deactivateChartPropDialog.assignment_id
          )
        }
        isPending={removeChartPropertyAssignmentMutation.isPending}
        actionText="Deactivate"
        actioningText="Deactivating…"
        titleAction="Deactivate"
        icon="disable"
      />
    </div>
  );
}
